import { OnboardingStepFooter } from "@/features/onboarding/components/OnboardingStepFooter";
import { formComponents as fc } from "@/features/i18n/formComponents";
import { ui } from "@patient-watch/ui";
import useUserUpdate from "@/features/profile/api/useUserMutation";
import type { ProfileT } from "@patient-watch/types/database";
import type { TitleT } from "@patient-watch/types";
import { titleOptions, TitleZ, FirstNameZ, LastNameZ } from "@patient-watch/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import type { SubmitHandler } from "react-hook-form";
import * as z from "zod";
import { useProfileCopy, type ProfileCopy } from "@/features/i18n/profileCopy";

export const NameSchemaZ = z.object({
    title: TitleZ,
    first_name: FirstNameZ,
    last_name: LastNameZ,
});

/** Same shape as {@link NameSchemaZ} but with locale-aware validation messages. */
export function makeNameSchema(copy: ProfileCopy) {
    return z.object({
        title: z.enum(titleOptions as [string, ...string[]], {
            errorMap: () => ({ message: copy.requiredField }),
        }),
        first_name: z.string().min(1, copy.firstNameRequired),
        last_name: z.string().min(1, copy.lastNameRequired),
    });
}

type FormSchemaType = z.infer<typeof NameSchemaZ>;

interface Props {
    userId: string;
    title: TitleT | null;
    firstName: ProfileT["first_name"];
    lastName: ProfileT["last_name"];
    autoFocus?: boolean;
    onSuccess?: () => void;
    onBack?: () => void;
    isDialog?: boolean;
    primaryLabel?: string;
}
const NameForm = (props: Props) => {
    const copy = useProfileCopy();
    const localizedSchema = makeNameSchema(copy);
    const form = useForm<FormSchemaType>({
        resolver: zodResolver(localizedSchema),
        defaultValues: {
            title: props?.title ?? undefined,
            first_name: props?.firstName ?? undefined,
            last_name: props.lastName ?? undefined,
        },
    });

    const userMutation = useUserUpdate(props.userId);

    const onSubmit: SubmitHandler<FormSchemaType> = async (formData) => {
        try {
            await userMutation.mutateAsync(formData);
            props.onSuccess?.();
        } catch {
            /* useUserMutation shows toast on error */
        }
    };

    return (
        <ui.Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="form">
                <fc.SelectTitle
                    autoFocus={props.autoFocus}
                    label={copy.titleLabel}
                    placeholder={copy.selectPlaceholder}
                    options={copy.titleOptions}
                />
                <fc.FirstName name="first_name" label={copy.firstName} />
                <fc.LastName name="last_name" label={copy.lastName} />
                {props.isDialog ? (
                    <fc.SaveButton isDialog={props.isDialog} />
                ) : (
                    <OnboardingStepFooter
                        onBack={props.onBack}
                        disabled={userMutation.isPending}
                        isLoading={userMutation.isPending}
                        primaryLabel={props.primaryLabel}
                    />
                )}
            </form>
        </ui.Form>
    );
};

export default NameForm;
